/* ==========================================================================
   Sticky Add to Cart controller — mobile & tablet, product pages only.

   Design
   ------
   Presentation only. It never creates a form, never calls /cart/add.js and
   never rebuilds the payload. On tap it proxies a click on the page's canonical
   Add to Cart button, so every existing flow runs unchanged:
     - standard products  -> button is type=submit -> theme AJAX add
     - fragrance gift set  -> validator hooks the original button click (capture)
     - tailored products   -> jQuery click handler on the original button
   Because all three custom flows hook the original button's CLICK (not the
   form's submit), proxying the click — not requestSubmit() — is the only method
   that preserves them.

   Visibility: an IntersectionObserver on the original button (no scroll
   polling). The bar shows when the original button is out of view, on a mobile/
   tablet viewport, and no overlay is open.

   Content: mirrored straight from the live DOM (price, button label, disabled
   state) — no currency maths, no re-implementation of variant logic.

   Lifecycle: one singleton, re-bootable after a Shopify section re-render.
   ========================================================================== */

(function () {
  'use strict';

  if (window.CaveStickyATC) return;

  var MOBILE_MAX = 990; // desktop (>= 990) disabled this phase
  var HTML = document.documentElement;

  var io = null;          // IntersectionObserver on the original button
  var htmlClassObs = null; // narrow observer: html class -> overlay state
  var wiredGlobals = false;
  var rafPending = false;
  var inFlight = false;    // guards against double submission
  var state = { inView: undefined }; // undefined until IO first fires

  // ---- element lookup (re-read every time; survives section re-render) ----
  function collect() {
    var root = document.querySelector('[data-cave-sticky-atc]');
    if (!root) return null;

    var form = document.getElementById(root.dataset.formId);
    var container = document.getElementById('ProductSection-' + root.dataset.sectionId);
    if (!form || !container) return null;

    var originalBtn = form.querySelector('[data-add-to-cart]');
    if (!originalBtn) return null;

    return {
      root: root,
      form: form,
      container: container,
      originalBtn: originalBtn,
      originalText: form.querySelector('[data-add-to-cart-text]'),
      originalPrice: container.querySelector('[data-product-price]'),
      comparePrice: container.querySelector('[data-compare-price]'),
      comparePriceWrap: container.querySelector('[data-product-price-wrap]'),
      stickyPrice: root.querySelector('[data-cave-sticky-price]'),
      stickyVariant: root.querySelector('[data-cave-sticky-variant]'),
      stickyBtn: root.querySelector('[data-cave-sticky-add]'),
      stickyBtnText: root.querySelector('[data-cave-sticky-add-text]'),
      showPrice: root.dataset.showPrice === 'true',
      showVariant: root.dataset.showVariant === 'true'
    };
  }

  function isMobile() {
    return window.innerWidth < MOBILE_MAX;
  }

  // ---- overlay detection: hide the bar whenever a conflicting UI is open ----
  function isOverlayOpen() {
    // Theme drawers (cart/nav/search/filter/availability) + modals set classes
    // on <html>. lock-scroll is added for every scroll-locking overlay.
    if (HTML.classList.contains('js-drawer-open')) return true;
    if (HTML.classList.contains('modal-open')) return true;
    if (HTML.classList.contains('lock-scroll')) return true;

    // Custom overlays that toggle their own element rather than an html class.
    var gift = document.getElementById('cave-checkout-popup');
    if (gift && !gift.hasAttribute('hidden')) return true;

    var gtl = document.getElementById('cave-gtl-drawer');
    if (gtl && !gtl.hasAttribute('hidden')) return true;

    // Tailoring modal + size-guide popups (full-screen, shown via inline style).
    var popups = document.querySelectorAll('.modaltailored, [popup-type]');
    for (var i = 0; i < popups.length; i++) {
      if (popups[i].offsetParent !== null) return true; // visible
    }
    return false;
  }

  // ---- mirror live DOM into the bar ----
  function syncContent(refs) {
    if (!refs) return;

    // Price (already Markets/sale-formatted by the theme).
    if (refs.showPrice && refs.stickyPrice && refs.originalPrice) {
      var html = '';
      var compareVisible =
        refs.comparePrice &&
        refs.comparePrice.textContent.trim() !== '' &&
        refs.comparePriceWrap &&
        !refs.comparePriceWrap.classList.contains('hide');
      if (compareVisible) {
        html += '<span class="cave-sticky-atc__compare">' +
          refs.comparePrice.textContent.trim() + '</span>';
      }
      html += refs.originalPrice.innerHTML;
      refs.stickyPrice.innerHTML = html;
    }

    // Selected variant name, built from the checked variant inputs.
    if (refs.showVariant && refs.stickyVariant) {
      var checked = refs.container.querySelectorAll('[data-variant-input]:checked');
      var parts = [];
      checked.forEach(function (el) {
        var v = (el.value || '').trim();
        if (v && parts.indexOf(v) === -1) parts.push(v);
      });
      var label = parts.join(' / ');
      if (label && label !== 'Default Title') {
        refs.stickyVariant.textContent = label;
        refs.stickyVariant.classList.remove('hide');
      } else {
        refs.stickyVariant.classList.add('hide');
      }
    }

    // Button label + state, mirrored from the canonical button.
    if (refs.stickyBtn) {
      var text = refs.originalText
        ? refs.originalText.textContent.trim()
        : refs.originalBtn.textContent.trim();
      var disabled =
        refs.originalBtn.disabled || refs.originalBtn.hasAttribute('disabled');
      var loading = refs.originalBtn.classList.contains('btn--loading');

      if (refs.stickyBtnText) refs.stickyBtnText.textContent = text;
      refs.stickyBtn.setAttribute('aria-label', text);
      refs.stickyBtn.disabled = disabled;
      refs.stickyBtn.classList.toggle('is-loading', loading);
    }
  }

  // ---- show / hide ----
  function setOpen(show, refs) {
    if (!refs) {
      HTML.classList.remove('cave-sticky-atc-open');
      return;
    }
    refs.root.hidden = !show;
    HTML.classList.toggle('cave-sticky-atc-open', !!show);
    if (show) {
      HTML.style.setProperty('--cave-sticky-atc-h', refs.root.offsetHeight + 'px');
    }
  }

  function recompute() {
    var refs = collect();
    if (!refs) {
      setOpen(false, null);
      return;
    }
    // inView undefined (IO not fired yet) is treated as "in view" so the bar
    // stays hidden on first paint — prevents load flicker.
    var outOfView = state.inView === false;
    var show = isMobile() && !isOverlayOpen() && outOfView;
    setOpen(show, refs);
    syncContent(refs);
    bindStickyButton(refs);
  }

  function deferRecompute() {
    if (rafPending) return;
    rafPending = true;
    requestAnimationFrame(function () {
      rafPending = false;
      recompute();
    });
  }

  // ---- submission: proxy the canonical button's click ----
  function bindStickyButton(refs) {
    var btn = refs.stickyBtn;
    if (!btn || btn.__caveBound) return;
    btn.__caveBound = true;

    btn.addEventListener('click', function (e) {
      e.preventDefault();

      var fresh = collect();
      if (!fresh) return;
      var orig = fresh.originalBtn;

      // Don't proxy a disabled button, an add already in flight (the theme sets
      // btn--loading synchronously on submit), or a re-entrant tap in the same
      // burst. Using the theme's own loading class means a validation-BLOCKED
      // tap — which never sets that class — does NOT lock the button, so the
      // customer can fix their selection and tap again immediately.
      if (orig.disabled || orig.hasAttribute('disabled')) return;
      if (orig.classList.contains('btn--loading')) return;
      if (inFlight) return;

      inFlight = true;

      // One submission method: replay a real click on the canonical button.
      // This runs the theme's AJAX add, the fragrance validator and the
      // tailoring handler exactly as a direct tap would.
      orig.click();

      // Short same-burst guard only. Real double-submit protection for the
      // whole AJAX window is the btn--loading check above plus the theme's own
      // internal loading flag. Kept brief so a validation-blocked retap is not
      // stalled.
      setTimeout(function () { inFlight = false; deferRecompute(); }, 400);
    });
  }

  // ---- (re)boot: (re)create the IntersectionObserver ----
  function boot() {
    var refs = collect();
    if (io) { io.disconnect(); io = null; }
    if (!refs) { setOpen(false, null); return; }

    io = new IntersectionObserver(function (entries) {
      state.inView = entries[0].isIntersecting;
      recompute();
    }, { root: null, rootMargin: '0px', threshold: 0 });
    io.observe(refs.originalBtn);

    if (!wiredGlobals) { wireGlobals(); wiredGlobals = true; }
    recompute();
  }

  function wireGlobals() {
    // Content-critical, low-frequency events are bound DIRECTLY (not through the
    // rAF coalescer) so price / variant / button state always sync immediately,
    // even if the tab is between paints and rAF is briefly deferred.
    document.addEventListener('variant:change', recompute);
    document.addEventListener('ajaxProduct:added', recompute);
    document.addEventListener('ajaxProduct:error', recompute);
    document.addEventListener('cart:build', recompute);

    // High-frequency: any click can open/close an overlay or change tailoring
    // price; recompute after handlers run, coalesced via rAF. Event-driven, not
    // polling.
    document.addEventListener('click', deferRecompute, true);

    // Theme drawers / modals — visibility-critical, bound directly so the bar
    // hides the instant an overlay opens (no dependency on rAF timing).
    ['drawerOpen', 'drawerClose', 'modalOpen', 'modalClose'].forEach(function (ev) {
      document.addEventListener(ev, recompute);
    });

    // One narrow observer on <html> class: the reliable signal for every theme
    // drawer/modal/lock-scroll toggle. Scoped to a single attribute on a single
    // node — not a broad subtree observer.
    htmlClassObs = new MutationObserver(deferRecompute);
    htmlClassObs.observe(HTML, { attributes: true, attributeFilter: ['class'] });

    // Viewport + navigation.
    window.addEventListener('resize', deferRecompute);
    window.addEventListener('orientationchange', deferRecompute);
    window.addEventListener('pageshow', boot); // bfcache back/forward
    document.addEventListener('shopify:section:load', boot); // theme editor
  }

  window.CaveStickyATC = { boot: boot, recompute: recompute };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
